import type { API } from '@/services/typings';

/**
 * Permission Utility Functions
 *
 * This module provides permission checking functions that mirror the backend logic in src/role.rs
 *
 * Key Concepts:
 * 1. Module-level permissions: Check if user has a permission (e.g., DEVICES.EDIT)
 * 2. Item-level permissions: Check if user can operate on a SPECIFIC item based on role type
 *
 * Role Types determine the type of permissions:
 * - GLOBAL (1): Access to all items in the team
 * - INDIVIDUAL (2): Access only to items assigned to the user
 * - GROUP (3): Access to items within specified groups (both user groups and device groups)
 *
 * Important: User.roles is now an array of Role objects containing:
 * - type: Role type (GLOBAL, INDIVIDUAL, GROUP)
 * - permissions: Array of permission IDs (module << 8 | level)
 * - user_groups: User group names for GROUP type
 * - device_groups: Device group names for GROUP type
 * - unassigned: Whether unassigned devices are included (for GROUP type)
 *
 * Note: Backend uses group_guid internally but serializes group names to frontend for
 * display and comparison. Frontend only uses group names to match items.
 *
 * Permission Constants and Types
 * Must stay in sync with backend role.rs
 */

// Role Types
export enum RoleType {
  GLOBAL = 1,  // Team level (Global)
  INDIVIDUAL = 2,    // Individual user level
  GROUP_SCOPED = 3,  // Group Scoped level (manages both users and devices in groups)
}

// Permission modules (bits 8-15)
export enum Module {
  USERS = 0x01,
  DEVICES = 0x02,
  USER_GROUPS = 0x03,
  DEVICE_GROUPS = 0x04,
  AUDIT_LOGS = 0x05,
  STRATEGIES = 0x06,
  CUSTOM_CLIENTS = 0x07,
  CONTROL_ROLES = 0x08,
}

// Permission levels (bits 0-7)
export enum Level {
  VIEW = 0x01,
  EDIT = 0x02,

  // Users module - fine-grained operations
  USER_CREATE = 0x03,
  USER_INVITE = 0x04,
  USER_DELETE = 0x05,
  USER_ENABLE_DISABLE = 0x06,
  USER_EDIT_EMAIL = 0x07,
  USER_EDIT_PASSWORD = 0x08,
  USER_EDIT_NOTE = 0x09,
  USER_MANAGE_2FA = 0x0A,
  USER_FORCE_LOGOUT = 0x0B,
  USER_CHANGE_GROUP = 0x0C,
  USER_CHANGE_STRATEGY = 0x0D,
  USER_CHANGE_CONTROL_ROLE = 0x0E,
  USER_EDIT_DISPLAY_NAME = 0x0F,

  // Devices module - fine-grained operations
  DEVICE_ENABLE_DISABLE = 0x03,
  DEVICE_DELETE = 0x04,
  DEVICE_EDIT_INFO = 0x05,
  DEVICE_ASSIGN_TO_USER = 0x06,
  DEVICE_CHANGE_GROUP = 0x07,
  DEVICE_CHANGE_STRATEGY = 0x08,

  // Device Groups module - fine-grained operations
  DEVICE_GROUP_CHANGE_STRATEGY = 0x03,
}

// Compute permission ID: (module << 8) | level
export const permId = (module: number, level: number): number => {
  return (module << 8) | level;
};

// Parse permission ID to extract module and level
export const parsePermId = (permId: number): { module: number; level: number } => {
  const module = (permId >> 8) & 0xFF;
  const level = permId & 0xFF;
  return { module, level };
};

/**
 * Check if any permission in a perm list matches module and levels
 * Mirrors backend: has_perm_in_list()
 */
const hasPermInList = (perms: number[] | undefined, module: Module, levels: number[]): boolean => {
  if (!perms) return false;
  for (const permId of perms) {
    const { module: permModule, level: permLevel } = parsePermId(permId);
    if (permModule === module && levels.includes(permLevel)) {
      return true;
    }
  }
  return false;
};

/**
 * Check if user has any permission matching module, levels, and types
 * Mirrors backend: has_any()
 */
const hasAny = (
  roles: API.AdminRole[],
  module: Module,
  levels: number[],
  types: RoleType[]
): boolean => {
  for (const role of roles) {
    if (types.includes(role.type) && hasPermInList(role.permissions, module, levels)) {
      return true;
    }
  }
  return false;
};

/**
 * Check if user has exact permission matching module, level, and type
 * Mirrors backend: has_exact()
 */
const hasExact = (
  roles: API.AdminRole[],
  module: Module,
  level: number,
  type: RoleType
): boolean => {
  return hasAny(roles, module, [level], [type]);
};

/**
 * Get user groups from roles that have the specified permission
 * Mirrors backend: get_user_groups()
 */
const getUserGroups = (
  roles: API.AdminRole[],
  module: Module,
  levels: number[]
): string[] => {
  const groups = new Set<string>();
  for (const role of roles) {
    if (role.type === RoleType.GROUP_SCOPED && hasPermInList(role.permissions, module, levels)) {
      for (const g of role.user_groups || []) {
        groups.add(g);
      }
    }
  }
  return Array.from(groups);
};

/**
 * Get device groups from roles that have the specified permission
 * Mirrors backend: get_device_groups()
 */
const getDeviceGroups = (
  roles: API.AdminRole[],
  module: Module,
  levels: number[]
): string[] => {
  const groups = new Set<string>();
  for (const role of roles) {
    if (role.type === RoleType.GROUP_SCOPED && hasPermInList(role.permissions, module, levels)) {
      for (const g of role.device_groups || []) {
        groups.add(g);
      }
    }
  }
  return Array.from(groups);
};

/**
 * Check if any GROUP role with the permission has unassigned=true
 * Mirrors backend: has_unassigned()
 */
const hasUnassigned = (
  roles: API.AdminRole[],
  module: Module,
  levels: number[]
): boolean => {
  for (const role of roles) {
    if (role.type === RoleType.GROUP_SCOPED && role.unassigned && hasPermInList(role.permissions, module, levels)) {
      return true;
    }
  }
  return false;
};


// ============================================================================
// Users Module (Fine-grained operations)
// ============================================================================

export const hasPermUsersCreate = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.USERS, [Level.USER_CREATE], [RoleType.GLOBAL, RoleType.GROUP_SCOPED]);
};

export const hasPermUsersInvite = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.USERS, [Level.USER_INVITE], [RoleType.GLOBAL, RoleType.GROUP_SCOPED]);
};

// ============================================================================
// Item-level permission helpers
// ============================================================================

/**
 * Check if current user can perform a specific operation on a user item
 * Mirrors backend logic in has_perm_user_operation_on_users()
 */
export const hasPermUserOperationOnItem = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined,
  level: number
): boolean => {
  if (!currentUser || !item) return false;
  if (currentUser.is_admin) return true;

  // Cannot operate on admin users regardless of permissions
  if (item.is_admin) return false;

  const roles = currentUser.admin_roles || [];

  // Check for GLOBAL type permission
  if (hasExact(roles, Module.USERS, level, RoleType.GLOBAL)) {
    return true;
  }

  // Check for GROUP type permission (user groups)
  if (!item.group_name) return false;

  const userGroups = getUserGroups(roles, Module.USERS, [level]);
  return userGroups.includes(item.group_name);
};

export const hasAnyPermUserOperation = (
  currentUser: API.User | undefined,
  level: number
): boolean => {
  if (!currentUser) return false;
  if (currentUser.is_admin) return true;

  const roles = currentUser.admin_roles || [];

  // Check for GLOBAL type permission
  if (hasExact(roles, Module.USERS, level, RoleType.GLOBAL)) {
    return true;
  }
  const userGroups = getUserGroups(roles, Module.USERS, [level]);
  return userGroups.length > 0;
};

// Convenience functions for specific operations
export const canDeleteUser = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_DELETE);
};

export const canEnableDisableUser = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_ENABLE_DISABLE);
};

export const canEditUserEmail = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_EDIT_EMAIL);
};

export const canEditUserPassword = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_EDIT_PASSWORD);
};

export const canEditUserNote = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_EDIT_NOTE);
};

export const canEditUserDisplayName = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_EDIT_DISPLAY_NAME);
};

export const canManageUser2FA = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_MANAGE_2FA);
};

export const canForceLogoutUser = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_FORCE_LOGOUT);
};

export const canChangeGroupOfUser = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_CHANGE_GROUP);
};

export const hasAnyPermChangeGroupOfUser = (
  currentUser: API.User | undefined,
): boolean => {
  if (!currentUser) return false;
  if (currentUser.is_admin) return true;
  return hasAny(currentUser?.admin_roles || [], Module.USERS, [Level.USER_CHANGE_GROUP], [RoleType.GLOBAL]);
};

export const canAssignStrategyToUser = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_CHANGE_STRATEGY);
};

export const hasAnyPermAssignStrategyToUser = (
  currentUser: API.User | undefined,
): boolean => {
  return hasAnyPermUserOperation(currentUser, Level.USER_CHANGE_STRATEGY);
};

export const canAssignControlRoleToUser = (
  currentUser: API.User | undefined,
  item: API.UserListItem | undefined
): boolean => {
  return hasPermUserOperationOnItem(currentUser, item, Level.USER_CHANGE_CONTROL_ROLE);
};

export const hasAnyPermAssignControlRoleToUser = (
  currentUser: API.User | undefined,
): boolean => {
  return hasAnyPermUserOperation(currentUser, Level.USER_CHANGE_CONTROL_ROLE);
};

// ============================================================================
// Devices Module (Fine-grained operations)
// ============================================================================


/**
 * Check if user can perform a specific device operation on a device item
 * @param currentUser - Current logged in user
 * @param item - Device item to check
 * @param level - Permission level (e.g., Level.DEVICE_DELETE, Level.DEVICE_ENABLE_DISABLE)
 * @returns true if current user can perform the operation on this item
 */
export const hasPermDeviceOperationOnItem = (
  currentUser: API.User | undefined,
  item: API.DeviceListItem | undefined,
  level: number
): boolean => {
  if (currentUser?.is_admin) return true;
  if (!item) return false;

  const roles = currentUser?.admin_roles || [];

  // Check for GLOBAL type permission
  if (hasAny(roles, Module.DEVICES, [level], [RoleType.GLOBAL])) {
    return true;
  }

  // Check for INDIVIDUAL type permission
  if (hasAny(roles, Module.DEVICES, [level], [RoleType.INDIVIDUAL])) {
    if (item.user_name === currentUser?.name) {
      return true;
    }
  }

  // Check for unassigned devices
  if (!item.user && !item.device_group_name) {
    if (hasUnassigned(roles, Module.DEVICES, [level])) {
      return true;
    }
  }

  // Check for GROUP type permission - device groups
  const deviceGroups = getDeviceGroups(roles, Module.DEVICES, [level]);
  if (item.device_group_name && deviceGroups.includes(item.device_group_name)) {
    return true;
  }

  // Check for GROUP type permission - user groups (can manage devices of users in the group)
  // item.group_name is the user's group name (the device owner's group)
  const userGroups = getUserGroups(roles, Module.DEVICES, [level]);
  if (item.group_name && userGroups.includes(item.group_name)) {
    return true;
  }

  return false;
};

export const hasGlobalPermDeviceOperation = (
  currentUser: API.User | undefined,
  level: number
): boolean => {
  if (currentUser?.is_admin) return true;

  const roles = currentUser?.admin_roles || [];

  // Check for GLOBAL type permission
  if (hasAny(roles, Module.DEVICES, [level], [RoleType.GLOBAL])) {
    return true;
  }

  return false;
};

// Convenience functions for specific device operations
export const canEnableDisableDevice = (
  currentUser: API.User | undefined,
  item: API.DeviceListItem | undefined
): boolean => {
  return hasPermDeviceOperationOnItem(currentUser, item, Level.DEVICE_ENABLE_DISABLE);
};

export const canDeleteDevice = (
  currentUser: API.User | undefined,
  item: API.DeviceListItem | undefined
): boolean => {
  return hasPermDeviceOperationOnItem(currentUser, item, Level.DEVICE_DELETE);
};

export const canEditDeviceInfo = (
  currentUser: API.User | undefined,
  item: API.DeviceListItem | undefined
): boolean => {
  return hasPermDeviceOperationOnItem(currentUser, item, Level.DEVICE_EDIT_INFO);
};

export const hasGlobalPermAssignDeviceToUser = (
  currentUser: API.User | undefined,
): boolean => {
  return hasGlobalPermDeviceOperation(currentUser, Level.DEVICE_ASSIGN_TO_USER);
};

export const hasGlobalPermChangeDeviceGroup = (
  currentUser: API.User | undefined,
): boolean => {
  return hasGlobalPermDeviceOperation(currentUser, Level.DEVICE_CHANGE_GROUP);
};


export const canAssignStrategyToDevice = (
  currentUser: API.User | undefined,
  item: API.DeviceListItem | undefined
): boolean => {
  return hasPermDeviceOperationOnItem(currentUser, item, Level.DEVICE_CHANGE_STRATEGY);
};

export const hasAnyPermToAssignStrategyToDevice = (
  currentUser: API.User | undefined,
): boolean => {
  if (currentUser?.is_admin) return true;
  const roles = currentUser?.admin_roles || [];
  return hasAny(roles, Module.DEVICES, [Level.DEVICE_CHANGE_STRATEGY], [RoleType.GLOBAL, RoleType.GROUP_SCOPED, RoleType.INDIVIDUAL]);
};


// ============================================================================
// User Groups Module (EDIT only)
// ============================================================================

export const hasPermUserGroupsEdit = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.USER_GROUPS, [Level.EDIT], [RoleType.GLOBAL]);
};

// ============================================================================
// Device Groups Module (EDIT only)
// ============================================================================

export const hasPermDeviceGroupsEdit = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.DEVICE_GROUPS, [Level.EDIT], [RoleType.GLOBAL]);
};

export const hasGlobalPermToAssignStrategyToDeviceGroup = (
  user?: API.User,
): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.DEVICE_GROUPS, [Level.EDIT, Level.DEVICE_GROUP_CHANGE_STRATEGY], [RoleType.GLOBAL]);
};

// ============================================================================
// Audits Module (VIEW and EDIT)
// ============================================================================

export const hasPermAuditsEdit = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.AUDIT_LOGS, [Level.EDIT], [RoleType.GLOBAL, RoleType.INDIVIDUAL]);
};



/**
 * Check if user can edit a specific audit log
 * Mirrors backend logic in has_perm_to_edit_this_audit()
 */
export const hasPermAuditsEditItem = (
  user: API.User | undefined,
  auditLog: API.ConnAuditListItem
): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;

  const roles = user.admin_roles || [];

  // Check for GLOBAL type permission - can edit all
  if (hasExact(roles, Module.AUDIT_LOGS, Level.EDIT, RoleType.GLOBAL)) {
    return true;
  }

  // Check for INDIVIDUAL type permission - can only edit own audits
  if (hasExact(roles, Module.AUDIT_LOGS, Level.EDIT, RoleType.INDIVIDUAL)) {
    // Note: Backend checks via DB query, here we approximate by checking usernames
    if (
      auditLog.remote_assigned_user === user.name ||
      auditLog.controller_user === user.name ||
      auditLog.local_assigned_user === user.name
    ) {
      return true;
    }
  }

  return false;
};

// ============================================================================
// Strategies Module (EDIT only)
// ============================================================================

export const hasPermStrategiesEdit = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.STRATEGIES, [Level.EDIT], [RoleType.GLOBAL]);
};

// ============================================================================
// Controlling Strategies Module (VIEW and EDIT)
// ============================================================================

export const hasPermControlRolesEdit = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.CONTROL_ROLES, [Level.EDIT], [RoleType.GLOBAL]);
};

// ============================================================================
// Custom Clients Module (EDIT only)
// ============================================================================

export const hasPermCustomClientsEdit = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.CUSTOM_CLIENTS, [Level.EDIT], [RoleType.GLOBAL]);
};

// view perms
export const hasPermToViewUsersInGroup = (user?: API.User, groupname?: string): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  if (groupname) {
    let levels = [
      Level.VIEW,
      Level.USER_CREATE,
      Level.USER_INVITE,
      Level.USER_DELETE,
      Level.USER_ENABLE_DISABLE,
      Level.USER_EDIT_EMAIL,
      Level.USER_EDIT_PASSWORD,
      Level.USER_EDIT_NOTE,
      Level.USER_EDIT_DISPLAY_NAME,
      Level.USER_MANAGE_2FA,
      Level.USER_FORCE_LOGOUT,
      Level.USER_CHANGE_GROUP,
      Level.USER_CHANGE_STRATEGY,
      Level.USER_CHANGE_CONTROL_ROLE,
    ];
    if (hasAny(user.admin_roles || [], Module.USERS, levels, [RoleType.GLOBAL])) {
      return true;
    }
    let groups = getUserGroups(user.admin_roles || [], Module.USERS, levels);
    return groups.includes(groupname);
  } else {
    return hasModuleAccess(user.admin_roles || [], Module.USERS);
  }
};

export const hasPermToViewDevicesInGroup = (user?: API.User, groupname?: string): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  if (groupname) {
    let levels = [
      Level.VIEW,
      Level.DEVICE_ENABLE_DISABLE,
      Level.DEVICE_DELETE,
      Level.DEVICE_EDIT_INFO,
      Level.DEVICE_ASSIGN_TO_USER,
      Level.DEVICE_CHANGE_GROUP,
      Level.DEVICE_CHANGE_STRATEGY,
    ];
    if (hasAny(user.admin_roles || [], Module.DEVICES, levels, [RoleType.GLOBAL])) {
      return true;
    }
    let groups = getDeviceGroups(user.admin_roles || [], Module.DEVICES, levels);
    return groups.includes(groupname);
  } else {
    return hasModuleAccess(user.admin_roles || [], Module.DEVICES);
  }
};

export const hasPermDeviceGroupsView = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasModuleAccess(user.admin_roles || [], Module.DEVICE_GROUPS);
};

export const shouldUserListShowMultiSelect = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [],
    Module.USERS,
    [
      Level.USER_ENABLE_DISABLE,
      Level.USER_EDIT_NOTE,
      Level.USER_MANAGE_2FA,
      Level.USER_FORCE_LOGOUT,
    ],
    [RoleType.GLOBAL, RoleType.GROUP_SCOPED]);
};

export const shouldDeviceListShowMultiSelect = (user?: API.User): boolean => {
  if (!user) return false;
  if (user.is_admin) return true;
  return hasAny(user.admin_roles || [], Module.DEVICES,
    [
      Level.DEVICE_ENABLE_DISABLE,
      Level.DEVICE_DELETE,
      Level.DEVICE_EDIT_INFO,
    ],
    [RoleType.GLOBAL, RoleType.GROUP_SCOPED, RoleType.INDIVIDUAL]);
};

// ============================================================================
// Recommended Permissions Helper
// ============================================================================

/**
 * Get recommended permissions that should be auto-added when a parent permission is selected
 * Returns a flat array of permission IDs
 * Note: Only GLOBAL role type has access to CONTROLLED_STRATEGIES and CONTROLLING_STRATEGIES modules
 */
export const getRecommendedPermissions = (module: number, level: number, roleType: RoleType = RoleType.GLOBAL): number[] => {
  const recommended: number[] = [];

  switch (module) {
    case Module.USERS:
      if (level === Level.USER_DELETE) {
        // Users Delete: Required - Enable/Disable Users
        recommended.push(permId(Module.USERS, Level.USER_ENABLE_DISABLE));
      } else if (level === Level.USER_CHANGE_STRATEGY) {
        // Only recommend CONTROLLED_STRATEGIES.VIEW for GLOBAL role type
        if (roleType === RoleType.GLOBAL) {
          recommended.push(permId(Module.STRATEGIES, Level.VIEW));
        }
      } else if (level === Level.USER_CHANGE_CONTROL_ROLE) {
        // Only recommend CONTROLLING_STRATEGIES.VIEW for GLOBAL role type
        if (roleType === RoleType.GLOBAL) {
          recommended.push(permId(Module.CONTROL_ROLES, Level.VIEW));
        }
      }
      break;

    case Module.DEVICES:
      if (level === Level.DEVICE_DELETE) {
        // Devices Delete: Required - Enable/Disable Devices (same scope)
        recommended.push(permId(Module.DEVICES, Level.DEVICE_ENABLE_DISABLE));
      } else if (level === Level.DEVICE_CHANGE_STRATEGY) {
        // Only recommend CONTROLLED_STRATEGIES.VIEW for GLOBAL role type
        if (roleType === RoleType.GLOBAL) {
          recommended.push(permId(Module.STRATEGIES, Level.VIEW));
        }
      }
      break;

    case Module.USER_GROUPS:
      if (level === Level.VIEW) {
        // User Groups View: Recommended - Users View
        recommended.push(permId(Module.USERS, Level.VIEW));
      } else if (level === Level.EDIT) {
        // User Groups Edit: Recommended - Users View
        recommended.push(permId(Module.USERS, Level.USER_CHANGE_GROUP));
      }
      break;

    case Module.DEVICE_GROUPS:
      if (level === Level.VIEW) {
        // Device Groups View: Recommended - Devices View
        recommended.push(permId(Module.DEVICES, Level.VIEW));
      } else if (level === Level.EDIT) {
        // Device Groups Edit: Recommended - Devices View
        recommended.push(permId(Module.DEVICES, Level.DEVICE_CHANGE_GROUP));
      } else if (level === Level.DEVICE_GROUP_CHANGE_STRATEGY) {
        recommended.push(permId(Module.STRATEGIES, Level.VIEW));
      }
      break;
    case Module.STRATEGIES:
      if (level === Level.VIEW) {
        recommended.push(permId(Module.DEVICES, Level.VIEW));
        recommended.push(permId(Module.USERS, Level.VIEW));
        recommended.push(permId(Module.DEVICE_GROUPS, Level.VIEW));
      } else if (level === Level.EDIT) {
        recommended.push(permId(Module.DEVICES, Level.DEVICE_CHANGE_STRATEGY));
        recommended.push(permId(Module.USERS, Level.USER_CHANGE_STRATEGY));
        recommended.push(permId(Module.DEVICE_GROUPS, Level.DEVICE_GROUP_CHANGE_STRATEGY));
      }
      break;
    case Module.CONTROL_ROLES:
      if (level === Level.VIEW) {
        recommended.push(permId(Module.USERS, Level.VIEW));
      } else if (level === Level.EDIT) {
        recommended.push(permId(Module.USERS, Level.USER_CHANGE_CONTROL_ROLE));
      }
      break;
  }

  return recommended;
};

// Helper function to check if user has any access to a module
export const hasModuleAccess = (
  roles: API.AdminRole[] | undefined,
  module: Module
): boolean => {
  if (!roles) return false;
  return roles.some(role => {
    return (role.permissions || []).some(permId => {
      const { module: permModule } = parsePermId(permId);
      return permModule === module;
    });
  });
};
